/**
 * Pluggable access-token storage. Each app provides its own implementation:
 *  - storefront: in-memory (refresh token lives in an httpOnly cookie, so a page reload re-bootstraps via /auth/refresh)
 *  - admin/vendor: same approach — never put long-lived secrets in localStorage.
 */
export interface TokenStore {
  getAccessToken(): string | null;
  setAccessToken(token: string | null): void;
  /**
   * Where the refresh token lives, for clients that have to hold it themselves.
   *
   * The three web apps leave these out: their refresh token is an httpOnly
   * cookie the browser attaches on its own, and JavaScript that could read it
   * could also leak it. A native app has no cookie jar, so it keeps the token in
   * the OS keystore and hands it over explicitly — which is why these are
   * optional rather than part of every store.
   */
  getRefreshToken?(): Promise<string | null>;
  setRefreshToken?(token: string | null): Promise<void>;
}

/** Default in-memory store — survives SPA navigation, lost on full reload (by design). */
export function createMemoryTokenStore(initial: string | null = null): TokenStore {
  let token = initial;
  return {
    getAccessToken: () => token,
    setAccessToken: (t) => {
      token = t;
    },
  };
}
